import React from 'react';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <>
      <SEO
        title="Terms of Service | AssetWise Business Consultants"
        description="Terms of Service for AssetWise Business Consultants - loans, real estate and legal advisory services."
        path="/terms"
      />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-cyan-600 hover:text-cyan-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>

            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to AssetWise Business Consultants ("AssetWise", "we", "our", "us"). By accessing or using{' '}
                  <a href="https://www.assetwisebusiness.com/" className="text-cyan-600 hover:text-cyan-700">
                    https://www.assetwisebusiness.com/
                  </a>
                  , you agree to be bound by these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Scope of Services</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  AssetWise acts as a facilitator and service provider offering:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Loan facilitation (home loans, LAP, SME/business loans, project loans)</li>
                  <li>Documentation and compliance assistance</li>
                  <li>Property and loan-related advisory</li>
                  <li>Liaison with banks, NBFCs, and financial institutions</li>
                </ul>
                <p className="text-gray-700 leading-relaxed font-semibold">
                  We are NOT a lender, NBFC, or financial institution.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">RBI Digital Lending Compliance Position</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>AssetWise does not disburse loans or act as a regulated lender</li>
                  <li>All loans are provided by RBI-regulated entities (banks/NBFCs) or authorized institutions</li>
                  <li>Final sanction, KYC, underwriting, interest rates, and disbursement are handled exclusively by the lender</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility & User Responsibility</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By using our services, you confirm:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>You are at least 18 years old</li>
                  <li>All information and documents submitted are true, complete, and up to date</li>
                  <li>You will not misuse the platform for fraudulent or unlawful purposes</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">No Guarantee of Loan Approval</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  AssetWise does not guarantee:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Loan approval</li>
                  <li>Loan amount or eligibility</li>
                  <li>Interest rates or tenure</li>
                  <li>Processing timelines</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  All decisions are solely at the discretion of the lender.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Fees & Payments</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Certain services may involve consultation or processing fees, which will be disclosed in advance</li>
                  <li>Third-party costs (legal, valuation, stamp duty, processing fees, etc.) are borne by the client</li>
                  <li>Fees once paid may be non-refundable, unless explicitly agreed</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Engagement</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You acknowledge:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>AssetWise connects you with third parties (banks, NBFCs, legal professionals, etc.)</li>
                  <li>Any agreement is directly between you and such third parties</li>
                  <li>We are not responsible for their actions, delays, or decisions</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Consent & Communication</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By submitting your details, you consent to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Being contacted via call, SMS, WhatsApp, and email</li>
                  <li>Sharing your data with relevant lenders and partners for processing</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  This consent overrides DND preferences solely for service-related communication.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
                <p className="text-gray-700 leading-relaxed">
                  All content on this website (text, branding, logos, visuals) is owned by AssetWise and protected under applicable laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  AssetWise shall not be liable for:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Loan rejection, delays, or changes in terms</li>
                  <li>Financial or business losses arising from decisions taken by users</li>
                  <li>Actions or omissions of third-party institutions</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination of Services</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We reserve the right to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Refuse or discontinue service</li>
                  <li>Block access in case of misuse, fraud, or non-compliance</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law & Jurisdiction</h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed by the laws of India, with jurisdiction in Maharashtra.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Last Updated: April 2026
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsPage;
